import { useState, useCallback } from 'react';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useAuth } from '@/contexts/AuthContext';
import { useCalendarNotes, useCalendarNoteActions } from '@/hooks/useCalendarNotes';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  Calendar,
  Plus,
  Trash2,
  Edit2,
  Save,
  X,
  Loader2,
  Search,
  ChevronLeft,
  ChevronRight,
  StickyNote,
  Clock,
  History,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';

// Stat Pill Component
const StatPill = ({ icon: Icon, label, value, color = "slate" }) => {
  const colorConfig = {
    slate: { bg: "bg-slate-50", icon: "bg-slate-100 text-slate-600", value: "text-slate-800" },
    indigo: { bg: "bg-indigo-50", icon: "bg-indigo-100 text-indigo-600", value: "text-indigo-700" },
    emerald: { bg: "bg-emerald-50", icon: "bg-emerald-100 text-emerald-600", value: "text-emerald-700" },
    amber: { bg: "bg-amber-50", icon: "bg-amber-100 text-amber-600", value: "text-amber-700" }
  };
  const cfg = colorConfig[color];

  return (
    <div className={`flex items-center gap-3 px-4 py-3 rounded-xl ${cfg.bg} border border-slate-200/50`}>
      <div className={`p-2 rounded-lg ${cfg.icon}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div>
        <p className={`text-xl font-bold ${cfg.value}`}>{value}</p>
        <p className="text-xs text-slate-500">{label}</p>
      </div>
    </div>
  );
};

// Note Row Component
const NoteRow = ({ note, isEditing, editingNote, onStartEdit, onUpdateNote, onCancelEdit, onEditChange, onDelete, processing }) => {
  const getAccentColor = () => {
    if (note.is_today) return 'bg-indigo-500';
    if (note.is_past) return 'bg-slate-300';
    return 'bg-emerald-500';
  };

  return (
    <div className={`group relative bg-white rounded-xl border border-slate-200/80 p-4 pl-5 transition-all duration-300 hover:shadow-md hover:border-slate-300 ${note.is_past ? 'opacity-70' : ''}`}>
      <div className={`absolute left-0 top-3 bottom-3 w-1 ${getAccentColor()} rounded-full`} />

      <div className="flex items-start gap-4">
        <div className={`p-2.5 rounded-xl ${note.is_today ? 'bg-indigo-50' : note.is_past ? 'bg-slate-100' : 'bg-slate-50'}`}>
          <Calendar className={`h-5 w-5 ${note.is_today ? 'text-indigo-600' : note.is_past ? 'text-slate-400' : 'text-slate-600'}`} />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <span className={`font-semibold ${note.is_past ? 'text-slate-500' : 'text-slate-800'}`}>
              {note.formatted_date}
            </span>
            {note.is_today && (
              <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-indigo-100 text-indigo-700">
                Today
              </span>
            )}
            {note.is_upcoming && !note.is_today && (
              <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700">
                Upcoming
              </span>
            )}
            {note.is_past && (
              <span className="text-xs font-medium px-2.5 py-1 rounded-full bg-slate-100 text-slate-500">
                Past
              </span>
            )}
          </div>

          {isEditing ? (
            <div className="space-y-3">
              <Textarea
                value={editingNote?.note_text || ''}
                onChange={(e) => onEditChange(e.target.value)}
                rows={3}
                className="w-full rounded-lg border-slate-200"
                disabled={processing}
              />
              <div className="flex gap-2">
                <Button size="sm" onClick={onUpdateNote} disabled={processing} className="rounded-full bg-slate-800 hover:bg-slate-700">
                  {processing ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Save className="h-3 w-3 mr-1" />}
                  Save
                </Button>
                <Button size="sm" variant="outline" onClick={onCancelEdit} disabled={processing} className="rounded-full">
                  <X className="h-3 w-3 mr-1" />
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <p className={`text-sm whitespace-pre-wrap ${note.is_past ? 'text-slate-500' : 'text-slate-700'}`}>
              {note.note_text}
            </p>
          )}
        </div>

        {!isEditing && (
          <div className="flex gap-1.5 opacity-0 group-hover:opacity-100 transition-opacity">
            <Button variant="ghost" size="sm" onClick={() => onStartEdit(note)} disabled={processing} className="h-8 w-8 p-0 rounded-lg">
              <Edit2 className="h-4 w-4 text-slate-500" />
            </Button>
            <Button variant="ghost" size="sm" onClick={() => onDelete(note.id)} disabled={processing} className="h-8 w-8 p-0 rounded-lg text-rose-500 hover:text-rose-600 hover:bg-rose-50">
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export const CalendarNotesPage = () => {
  const { userProfile, company, can } = useAuth();
  const canManageAttendance = can('attendance.manage');

  usePageTitle('Calendar Notes');

  const {
    notes,
    stats,
    loading,
    error,
    pagination,
    filters,
    refetch,
    years
  } = useCalendarNotes(company?.id);

  const {
    createNote,
    updateNote,
    deleteNote,
    processing
  } = useCalendarNoteActions(company?.id, userProfile?.id, refetch);

  const [editingNote, setEditingNote] = useState(null);
  const [newNote, setNewNote] = useState({ note_date: '', note_text: '' });
  const [showNewForm, setShowNewForm] = useState(false);

  const handleCreateNote = useCallback(async () => {
    const result = await createNote(newNote);
    if (result.success) {
      toast.success('Calendar note added');
      setNewNote({ note_date: '', note_text: '' });
      setShowNewForm(false);
    } else {
      toast.error(result.error || 'Failed to create note');
    }
  }, [createNote, newNote]);

  const handleUpdateNote = useCallback(async () => {
    if (!editingNote) return;

    const result = await updateNote(editingNote.id, editingNote.note_text);
    if (result.success) {
      toast.success('Calendar note updated');
      setEditingNote(null);
    } else {
      toast.error(result.error || 'Failed to update note');
    }
  }, [updateNote, editingNote]);

  const handleDeleteNote = useCallback(async (noteId) => {
    if (!confirm('Are you sure you want to delete this calendar note?')) {
      return;
    }

    const result = await deleteNote(noteId);
    if (result.success) {
      toast.success('Calendar note deleted');
    } else {
      toast.error(result.error || 'Failed to delete note');
    }
  }, [deleteNote]);

  const handleCancelNew = useCallback(() => {
    setShowNewForm(false);
    setNewNote({ note_date: '', note_text: '' });
  }, []);

  const handleCancelEdit = useCallback(() => {
    setEditingNote(null);
  }, []);

  const handleStartEdit = useCallback((note) => {
    setEditingNote({ ...note });
  }, []);

  const handleNewNoteChange = useCallback((field, value) => {
    setNewNote(prev => ({ ...prev, [field]: value }));
  }, []);

  const handleEditNoteChange = useCallback((value) => {
    setEditingNote(prev => prev ? { ...prev, note_text: value } : null);
  }, []);

  if (!canManageAttendance) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-amber-50 flex items-center justify-center">
            <AlertCircle className="h-7 w-7 text-amber-500" />
          </div>
          <p className="text-slate-800 font-semibold">Access Restricted</p>
          <p className="text-slate-500 text-sm mt-1">You do not have permission to manage calendar notes</p>
        </div>
      </div>
    );
  }

  if (loading && !notes.length) {
    return (
      <div className="flex items-center justify-center h-96">
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex flex-col items-center gap-4">
          <div className="relative">
            <div className="w-16 h-16 rounded-full border-4 border-slate-100" />
            <motion.div
              className="absolute inset-0 w-16 h-16 rounded-full border-4 border-transparent border-t-slate-500"
              animate={{ rotate: 360 }}
              transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
            />
          </div>
          <p className="text-slate-500 font-medium">Loading notes...</p>
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-rose-50 flex items-center justify-center">
            <AlertCircle className="h-7 w-7 text-rose-500" />
          </div>
          <p className="text-slate-800 font-semibold">Failed to load notes</p>
          <p className="text-slate-500 text-sm mt-1">{error}</p>
          <Button onClick={refetch} className="mt-4 rounded-full">Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl">
      {/* Hero Section */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="rounded-2xl bg-white border border-slate-200 p-8"
      >
        <div className="flex items-start justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-800 tracking-tight">Calendar Notes</h1>
            <p className="text-slate-500 mt-1">
              Add admin-only notes to specific dates. Visible only to attendance managers.
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatPill icon={StickyNote} label="Total Notes" value={stats.total} color="slate" />
          <StatPill icon={Calendar} label="This Year" value={stats.this_year} color="indigo" />
          <StatPill icon={Clock} label="Upcoming" value={stats.upcoming} color="emerald" />
          <StatPill icon={History} label="Past" value={stats.past} color="amber" />
        </div>
      </motion.div>

      {/* Add New Note Section */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.1 }}
        className="rounded-2xl bg-white border border-slate-200 p-6"
      >
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-lg bg-slate-100">
            <Plus className="h-4 w-4 text-slate-600" />
          </div>
          <div>
            <h3 className="font-semibold text-slate-800">Add New Note</h3>
            <p className="text-sm text-slate-500">Create a note for a specific date</p>
          </div>
        </div>

        {showNewForm ? (
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label>Date</Label>
              <Input
                type="date"
                value={newNote.note_date}
                onChange={(e) => handleNewNoteChange('note_date', e.target.value)}
                disabled={processing}
                className="rounded-lg max-w-xs"
              />
            </div>

            <div className="space-y-2">
              <Label>Note</Label>
              <Textarea
                placeholder="Enter note text..."
                value={newNote.note_text}
                onChange={(e) => handleNewNoteChange('note_text', e.target.value)}
                rows={3}
                disabled={processing}
                className="rounded-lg"
              />
            </div>

            <div className="flex gap-2">
              <Button onClick={handleCreateNote} disabled={processing || !newNote.note_date || !newNote.note_text.trim()} className="rounded-full bg-slate-800 hover:bg-slate-700">
                {processing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                Save Note
              </Button>
              <Button variant="outline" onClick={handleCancelNew} disabled={processing} className="rounded-full">
                <X className="h-4 w-4 mr-2" />
                Cancel
              </Button>
            </div>
          </div>
        ) : (
          <Button onClick={() => setShowNewForm(true)} variant="outline" className="rounded-full border-dashed border-2">
            <Plus className="h-4 w-4 mr-2" />
            Add Calendar Note
          </Button>
        )}
      </motion.div>

      {/* Notes List */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className="rounded-2xl bg-white border border-slate-200 p-6"
      >
        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search notes..."
              value={filters.search}
              onChange={(e) => filters.setSearch(e.target.value)}
              className="pl-10 h-10 rounded-full border-slate-200"
            />
          </div>
          <Select value={filters.year} onValueChange={filters.setYear}>
            <SelectTrigger className="w-full sm:w-[140px] h-10 rounded-full border-slate-200">
              <SelectValue placeholder="Year" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Years</SelectItem>
              {years.map((year) => (
                <SelectItem key={year.value} value={year.value}>{year.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* List Header */}
        <div className="flex items-center justify-between mb-4">
          <p className="text-sm text-slate-500">
            {pagination.filteredItems === 0 ? 'No calendar notes found' : `${pagination.filteredItems} note${pagination.filteredItems !== 1 ? 's' : ''}`}
            {filters.year !== 'all' && ` for ${filters.year}`}
            {filters.search && ` matching "${filters.search}"`}
            {loading && <Loader2 className="inline h-4 w-4 ml-2 animate-spin" />}
          </p>
        </div>

        {/* Notes */}
        {notes.length === 0 ? (
          <div className="text-center py-12">
            <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-slate-50 flex items-center justify-center">
              <Calendar className="h-7 w-7 text-slate-300" />
            </div>
            <p className="text-slate-500 font-medium">No calendar notes found</p>
            <p className="text-slate-400 text-sm mt-1">
              {(filters.year !== 'all' || filters.search) ? 'Try adjusting your filters' : 'Add your first note to get started'}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {notes.map((note) => (
              <NoteRow
                key={note.id}
                note={note}
                isEditing={editingNote?.id === note.id}
                editingNote={editingNote}
                onStartEdit={handleStartEdit}
                onUpdateNote={handleUpdateNote}
                onCancelEdit={handleCancelEdit}
                onEditChange={handleEditNoteChange}
                onDelete={handleDeleteNote}
                processing={processing}
              />
            ))}
          </div>
        )}

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="flex items-center justify-between mt-6 pt-4 border-t border-slate-100">
            <p className="text-sm text-slate-500">
              Page {pagination.page} of {pagination.totalPages}
            </p>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => pagination.setPage(pagination.page - 1)}
                disabled={pagination.page === 1 || loading}
                className="rounded-full"
              >
                <ChevronLeft className="h-4 w-4 mr-1" />
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => pagination.setPage(pagination.page + 1)}
                disabled={pagination.page >= pagination.totalPages || loading}
                className="rounded-full"
              >
                Next
                <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};
